/**
 * Password rules include:
 * - Minimum 15 characters
 * - At least one lowercase character
 * - At least one uppercase character
 * - At least one number
 * - No predictable patterns:
 *  a. No more than 3 identical characters in a row (e.g., "aaa", "111").
 *  b. No more than 3 consecutive characters in a sequence (e.g., "abc", "123", "cde").
 */

export function hasIdenticalRun(password: string, runLength = 4) {
  const regex = new RegExp(String.raw`(.)\1{${runLength - 1},}`);

  return regex.test(password);
}

export function hasSequentialRun(password: string, runLength = 4) {
  const alphabetRegex = /[a-zA-Z]/;
  const digitRegex = /\d/;

  for (let i = 0; i <= password.length - runLength; i++) {
    let isSeq = true;
    for (let j = 1; j < runLength; j++) {
      const prev = password.codePointAt(i + j - 1)!;
      const curr = password.codePointAt(i + j)!;
      const bothDigits =
        digitRegex.test(password[i + j - 1]) &&
        digitRegex.test(password[i + j]);
      const bothLetters =
        alphabetRegex.test(password[i + j - 1]) &&
        alphabetRegex.test(password[i + j]);
      if (!(bothDigits || bothLetters) || curr !== prev + 1) {
        isSeq = false;
        break;
      }
    }
    if (isSeq) return true;
  }

  return false;
}

export function validatePassword(password?: string) {
  if (!password)
    return {
      satisfiesMinimumCharacters: false,
      satisfiesCapitalCharacter: false,
      satisfiesLowercaseCharacter: false,
      satisfiesNumberCharacter: false,
      satisfiesSpecialCharacter: false,
      satisfiesNoIdenticalRun: false,
      satisfiesNoSequentialRun: false,
    };
  else {
    return {
      satisfiesMinimumCharacters: password.length >= 15,
      satisfiesCapitalCharacter: /[A-Z]/.test(password),
      satisfiesLowercaseCharacter: /[a-z]/.test(password),
      satisfiesNumberCharacter: /\d/.test(password),
      satisfiesSpecialCharacter: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(
        password,
      ),
      satisfiesNoIdenticalRun: !hasIdenticalRun(password),
      satisfiesNoSequentialRun: !hasSequentialRun(password),
    };
  }
}

export function passwordsValidation(p1?: string, p2?: string) {
  const validations = validatePassword(p1);
  const isPasswordValid =
    Object.values(validations).length > 0 &&
    Object.values(validations).every((validation) => !!validation);
  const isMatching = p1 === p2;
  const result = {
    validations,
    isMatching,
    isPasswordValid,
    allValid: isPasswordValid && isMatching,
  };

  return result;
}
